import $ from "jquery";
import escapeHtml from "./util/escape_html";

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const activeSubRequired = "Active subscription required.";
const DELAY_OPTIONS = { "show": 200, "hide": 500 };
// Room (px) an icon tooltip needs below it before we flip it above. Covers the tallest icon
// tooltip — the `.activity` description balloon (~220px). See iconTooltipPlacement below.
const TOOLTIP_FLIP_MARGIN = 250;

// Decide an icon-tooltip's placement against the real window, preferring "bottom".
//
// We CANNOT use Bootstrap 3's `placement: "auto bottom"` here: its auto-flip measures the viewport
// as document.body's box (getPosition($viewport), $viewport defaults to body), but the app pins
// `html, body { height: 100% }` (pure-drawer/_pure-drawer-global.scss — the sole html/body height
// rule). That makes body's border-box exactly one viewport tall, so on a scrolled page
// body.getBoundingClientRect().bottom goes small/negative and the flip test
// `pos.bottom + actualHeight > viewportDim.bottom` is true for ANY icon — "bottom" wrongly flips to
// "top" and the tooltip paints off the top of the viewport. window.innerHeight is the correct
// fixed reference. Mirrors the window-relative placement function already used in createCardTooltips.
const iconTooltipPlacement = function (_tip, element) {
  const rect = element.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  return spaceBelow < TOOLTIP_FLIP_MARGIN && rect.top > spaceBelow ? "top" : "bottom";
};
const DATAHUB_TABLE_TOOLTIPS = {
  "utilities": "utility",
  "private-investment-projects": "project",
  "tariff-survey": "city",
  "mergers-acquisitions": "acquisition"
};
const TAG_ICON_CLASS_TITLES = {
  TechnologyCategory: "Technologies",
  SectorType: "Industries",
  IndustrialSector: "Industries"
};

const PENDING_PAGES = ["Sludge management", "Smart water systems", "Equipment", "Equipment", "Industrial services"];

const titleForTagIconClass = function (classname) {
  return TAG_ICON_CLASS_TITLES[classname] || "";
};

const createTooltipOptions = function (title, placement, delayed = false, html = false) {
  const opts = {
    // BS5 rejects an undefined title (type must be string|element|function); createCardTooltips
    // passes $e.data("tooltip-content"), which is undefined for [data-has-tooltip] elements that
    // carry no content. "" is BS5-valid and matches BS3's old empty-tooltip behaviour.
    title: title || "",
    placement,
    html,
    // BS5 strictly type-checks `delay` as (number|object) and rejects null (BS3 tolerated it).
    // 0 is BS5's default (no delay) — behaviour-equivalent to the old null branch.
    delay: delayed ? DELAY_OPTIONS : 0,
    trigger: "hover"
  };
  return opts;
};

const createTooltips = function () {
  const options = {
    // .water-revenue / .demo-disable-table-header used to get their own delegated tooltip via a
    // second `$(document.documentElement).tooltip(...)` call below (createWaterRevenueTooltips).
    // Bootstrap 5 allows only ONE component instance per element -- getOrCreateInstance() silently
    // returns the instance from the FIRST call and ignores every later call's config entirely -- so
    // that second call was a complete no-op: the water-revenue selector/title never took effect, and
    // hovering the revenue-tier icon showed nothing. Merged into this one delegation instead.
    selector: "*[data-element-has-tooltip], .water-revenue, .demo-disable-table-header",
    placement: iconTooltipPlacement,
    html: true,
    container: "body",
    title() {
      const el = $(this);

      if (el.is(".water-revenue, .demo-disable-table-header")) {
        if (el.hasClass("demo-disable-table-header")) {
          return activeSubRequired;
        }

        const fiscalYear = el.data("size-year");
        const revenueNote = el.data("size-description");
        const ebitdaYear = el.data("ebitda-year");
        if (fiscalYear || revenueNote) {
          return `
            <span>${fiscalYear > 0 ? fiscalYear : ""} ${revenueNote ? revenueNote == "GWI" ? revenueNote + " estimate" : revenueNote : ""}</span>
          `;
        } else if (ebitdaYear) {
          return `
            <span>${ebitdaYear} Reported</span>
          `;
        }

        return "";
      }

      const category = titleForTagIconClass($(this).data("class"));
      if ($(this).hasClass("activity")) {
        const description = $(this).data("description");
        return `
          ${category}
          <div class="tooltip-large-content">${escapeHtml(description)}</div>
        `;
      } else if ($(this).parent().prop("tagName") == "A") {
        const title = $(this).data("icon-title");
        if (isMobile) {
          const hyperlink = $(this).parent().attr("href");
          return `
            <div class="tooltip-large-content">${escapeHtml(title)}</div>
            <a class="btn btn-default" href="${hyperlink}" target="_blank">Go to page</a>
          `;
        } else {
          return `
            <div class="tooltip-large-content">${escapeHtml(title)}</div>
            (click to go to the page)
          `;
        }
      } else {
        const dataTitle = $(this).data("icon-title");
        const title = dataTitle == "Utility" ? "Municipal" : dataTitle;
        const notice = PENDING_PAGES.includes(title) ? "(coming soon)" : "";
        return `
          <div class="tooltip-large-content">${escapeHtml(title)}</div>
          ${notice}
        `;
      }
    }
  };
  // Cannot be called more than once on the same element.
  // Delegate from the <html> element, NOT <body> and NOT document. (a) `document` has no
  // .dataset so constructing a BS5 component on it throws ("Cannot convert undefined or null
  // to object"), aborting the rest of the turbolinks:load chain incl. the global select2 init.
  // (b) With bootstrap-sass removed, scrollspy.js's `body.scrollspy()` now binds a
  // real bs.scrollspy instance to <body>, and BS5 allows only ONE instance per element — a
  // second body-bound component (this tooltip) console.errors "more than one instance".
  // <html> (documentElement) is a real element (has .dataset), is an ancestor of every
  // [data-element-has-tooltip] descendant so delegation still covers them, and is free of the
  // scrollspy instance. `container: "body"` keeps the popup appended to body (unchanged).
  $(document.documentElement).tooltip(options);
};

const createCardTooltips = function () {
  $("[data-has-tooltip]").each((i, e) => {
    const $e = $(e);
    $e.tooltip(createTooltipOptions(
      $e.data("tooltip-content"),
      function (context, source) {
        var position = $(source).position();

        if (position.top < 60){
          return "bottom";
        }

        return "top";
      },
      $e.data("tooltip-delayed"),
      $e.data("tooltip-html")
    ));
  });
};

const negateIconLinksOnMobile = () => {
  if(isMobile) {
    let $icons = [];
    const checkIcons = setInterval(() => {
      if ($(".tag-icon-link").length > $icons.length) {
        $icons = $(".tag-icon-link");
      } else {
        $(".tag-icon-link").on("click", e => {
          e.preventDefault();
        });
        clearInterval(checkIcons);
      }
    }, 2000);
  }
};

const createUpcomingProjectTooltips = function () {
  $(".upcoming-projects.card-container .effective-datatable").tooltip({
    selector: "tbody tr",
    html: true,
    title: "See more information about this project<br>(GWI subscription required)"
  });
  $(".upcoming-projects.card-container .right-controls a").tooltip({
    html: true,
    title: "See more projects<br>(GWI subscription required)"
  });
};

const createBOTTooltips = function () {
  $(".bot.card-container .effective-datatable").tooltip({
    selector: "tbody tr",
    title: "See more information about this company"
  });
};

const createDataHubTooltips = function () {
  for (const [key, value] of Object.entries(DATAHUB_TABLE_TOOLTIPS)) {
    $(`body.${key} .effective-datatable`).tooltip({
      selector: "tbody tr",
      title: `See more information about this ${value}`
    });
  }
};

const createSeeOrDownloadDataTooltips = function () {
  $(".card.chart").tooltip({
    selector: ".download-card, .see-data-button",
    trigger: "hover",
    title() {
      if ($(this).is(".demo-disabled")) {
        return activeSubRequired;
      } else {
        if ($(this).is(".see-data-button")) {
          return "Expand card and view data for this chart";
        } else {
          return "Download data for this chart";
        }
      }
    }
  });
};

const createExpandCardTooltips = function() {
  const expands = $("[data-expand]");
  expands.tooltip({
    title() {
      if($(this).closest("[data-card]").data("card-expanded")) {
        return "Collapse card";
      } else {
        return "Expand this card to view all content";
      }
    }
  });
  expands.on("click", function() {
    $(this).tooltip("hide");
  });
};

const createTechnologyPopovers = function() {
  $(".octopus-main-content").on("click", function() {
    const $this   = $(this);
    const $modal  = $("#technologiesModal");
    const type  = $this.data("technology-type");
    const title   = $this.find(".octo-text").text();
    const content = $(this).find(".tooltip-text").text();

    $modal.attr("data-technology-type", type);

    $modal.find(".modal-title").text(title);
    $modal.find(".modal-body").text(content);

    $("#technologiesModal").modal("show");
  });
};

const createDdRestrictedTooltips = function() {
  $(".dd-no-access .effective-datatable").tooltip({
    selector: "tbody tr",
    title: "GWI subscription required"
  });
};

const helpSupportTooltip = function () {
  const options = {
    selector: "#tooltip-anchor",
    // BS5/Popper rejects compound "auto top"; "top" + BS5's default fallbackPlacements
    // (top→right→bottom→left) gives the same prefer-top-then-auto-flip behaviour.
    placement: "top",
    html: true,
    container: "body",
    title() {
      return ("Connect on LinkedIn &#10132;");
    }
  };

  $(".h-s-tooltip").tooltip(options);
};

$(document).on("turbolinks:load", function () {
  createTooltips();
  createUpcomingProjectTooltips();
  createBOTTooltips();
  createCardTooltips();
  createDataHubTooltips();
  createSeeOrDownloadDataTooltips();
  createExpandCardTooltips();
  createTechnologyPopovers();
  createDdRestrictedTooltips();
  negateIconLinksOnMobile();
  helpSupportTooltip();
});

// Triggered by _card_body.html.erb
$(window).on("octoChartLoaded", createTechnologyPopovers);
